export type TableColumnType = 'checkbox' | 'sortable' | 'text' | 'actions';

export interface TableColumnAction {
  label: string;
  command: (rowData: unknown) => void;
  disabled?: boolean | ((rowData: unknown) => boolean);
}

interface TableColumnConfigBase {
  type: TableColumnType;
  label?: string;
  cssClass?: string;
  field?: string;
  width?: string;
  stopClickPropagation?: boolean;
  actions?: TableColumnAction[] | ((rowData: unknown) => TableColumnAction[]);
  /** Pins the column to the right edge of the scrollable table container (e.g. an actions column). */
  sticky?: boolean;
}

// `minWidth` is required when `resizable` is true so resize can be clamped to a lower bound.
// `field` is also required in that branch since the resize handlers no-op without it.
export type TableColumnConfig =
  | (TableColumnConfigBase & { resizable?: false; minWidth?: string })
  | (TableColumnConfigBase & { resizable: true; field: string; minWidth: string });

/** Resolves a column's effective width: a live drag-resize `px` override wins, then a `%` override carried over from the last pixel-mode layout, then the configured default. */
export function getResolvedColumnWidth(
  col: TableColumnConfig,
  columnSizing: Record<string, number>,
  columnWidthPercentages?: Record<string, string>
): string | undefined {
  if (col.field) {
    const overridePx = columnSizing[col.field];
    if (overridePx !== undefined) return `${overridePx}px`;

    const overridePercent = columnWidthPercentages?.[col.field];
    if (overridePercent !== undefined) return overridePercent;
  }
  return col.width;
}
